import { Card, CardContent, Typography, Divider } from '@mui/material';
import DataLabelPairing, {
    DataLabelPairingProps,
} from 'components/DataLabelPairing/DataLabelPairing';
import { FormSection } from 'components/form';
import { FormSelectOption } from 'components/form/types';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { BulkDevicesInput } from '../../BulkEditDevicesModal';
import styles from './Summary.module.scss';

export function BulkEditDevicesSummary() {
    const { watch } = useFormContext();
    const device = watch() as BulkDevicesInput;

    const transmissionTimeOptions: FormSelectOption[] = useMemo(() => {
        let currentTime = new Date();

        return [
            { value: -1, name: 'Default' },
            ...Array.from({ length: 24 }, () => {
                const value = currentTime.getUTCHours() * 3600;
                const out = {
                    name: new Date(
                        currentTime.setMinutes(0, 0, 0)
                    ).toLocaleTimeString('en-us', {
                        hour: 'numeric',
                        minute: '2-digit',
                    }),
                    value,
                };
                currentTime = new Date(currentTime.getTime() + 60 * 60 * 1000);
                return out;
            }),
        ];
    }, []);

    const timeOptions = [
        { value: 1 * 60 * 60, name: '1 Hour' },
        { value: 2 * 60 * 60, name: '2 Hours' },
        { value: 3 * 60 * 60, name: '3 Hours' },
        { value: 4 * 60 * 60, name: '4 Hours' },
        { value: 6 * 60 * 60, name: '6 Hours' },
        { value: 12 * 60 * 60, name: '12 Hours' },
        { value: 24 * 60 * 60, name: '24 Hours (default)' },
        { value: 48 * 60 * 60, name: '48 Hours' },
        { value: 72 * 60 * 60, name: '72 Hours' },
        { value: 96 * 60 * 60, name: '96 Hours' },
    ];

    const convertToTimeOption = (time: number) => {
        const timeOption = timeOptions.find((o) => o.value === time);
        if (timeOption) {
            return timeOption.name;
        }
        return `${time / 60 / 60} Hours`;
    };

    return (
        <FormSection title="Summary">
            <Card className={styles.details}>
                <CardContent>
                    <Typography variant="h5">
                        Confirm new settings for selected devices{' '}
                    </Typography>
                    <Divider textAlign="left">Alert Settings</Divider>
                    <BulkEditDataLabelPairing
                        label="Added Subscribers"
                        data={device.addedSubscribers?.value.join(', ')}
                        isShown={device.users.enabled}
                    />
                    <BulkEditDataLabelPairing
                        label="Removed Subscribers"
                        data={device.removedSubscribers?.value.join(', ')}
                        isShown={device.users.enabled}
                    />
                    <BulkEditDataLabelPairing
                        label="Change of Tank Level Since Last Transmission (%)"
                        data={device.tcov.value}
                        isShown={device.tcov.enabled}
                    />
                    <BulkEditDataLabelPairing
                        label="Warning Threshold  (%)"
                        data={device.thresh1.value}
                        isShown={device.thresh1.enabled}
                    />
                    <BulkEditDataLabelPairing
                        label="Critical Threshold (%)"
                        data={device.thresh2.value}
                        isShown={device.thresh2.enabled}
                    />
                    <BulkEditDataLabelPairing
                        label="Alerts Enabled"
                        data={device.enabled.value ? 'true' : 'false'}
                        isShown={device.enabled.enabled}
                    />
                    <Divider textAlign="left">Device Settings</Divider>
                    <BulkEditDataLabelPairing
                        label="Group"
                        data={device.group.value}
                        isShown={device.group.enabled}
                    />
                    <BulkEditDataLabelPairing
                        label="Transmission Time"
                        data={
                            transmissionTimeOptions.find(
                                (o) => o.value === device.ftt.value
                            )?.name
                        }
                        isShown={device.ftt.enabled}
                    />
                    <Divider textAlign="left">
                        Reading & Reporting Settings
                    </Divider>
                    <BulkEditDataLabelPairing
                        label="Reading Interval"
                        data={convertToTimeOption(device.r_int.value)}
                        isShown={device.r_int.enabled}
                    />
                    <BulkEditDataLabelPairing
                        label="Reporting Interval"
                        data={convertToTimeOption(device.t_int.value)}
                        isShown={device.t_int.enabled}
                    />
                </CardContent>
            </Card>
        </FormSection>
    );
}

interface BulkEditDataLabelPairingProps extends DataLabelPairingProps {
    isShown: boolean;
}

const BulkEditDataLabelPairing = ({
    data,
    label,
    isShown,
}: BulkEditDataLabelPairingProps) => {
    return (
        <DataLabelPairing label={label} data={isShown ? data : 'unchanged'} />
    );
};
